import React from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, IconButton, IconClose, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {TemplateTriggerReference} from "../../apis/requests/templates/types";
import {mutedButtonSx} from "../triggers/triggerUi";
import {TriggerReferenceRow} from "./TemplateTriggeredByPanel";

export type TemplateInUse = {id: number; name: string; triggers: TemplateTriggerReference[]};

export type TemplateInUseAction = "delete" | "deactivate" | "draft" | "channel" | "type";

const ACTION_TEXT: Record<TemplateInUseAction, {verb: string; title: string}> = {
  delete: {verb: "deleted", title: "Delete template"},
  deactivate: {verb: "deactivated", title: "Deactivate template"},
  draft: {verb: "moved to Draft", title: "Move template to Draft"},
  channel: {verb: "switched to this channel", title: "Change channel"},
  type: {verb: "changed from Transactional", title: "Change type"},
};

type Props = {
  action: TemplateInUseAction;
  templates: TemplateInUse[];
};

const TemplateInUseDialog = NiceModal.create(({action, templates}: Props) => {
  const modal = useModal();
  const {verb, title} = ACTION_TEXT[action];
  const single = templates.length === 1;
  const message = single
    ? `"${templates[0].name}" is still used by a trigger and cannot be ${verb} yet.`
    : `${templates.length} templates are still used by triggers and cannot be ${verb} yet.`;

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      slotProps={{
        transition: {onExited: () => modal.remove()},
        paper: {sx: {borderRadius: "16px", maxWidth: 640, width: "100%", backgroundColor: Colors.bw.white}},
      }}
      maxWidth={false}
    >
      <DialogTitle sx={{p: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Text size={20} variant="interDisplay600" sx={{color: Colors.grey._800}}>
          {title}
        </Text>
        <IconButton onClick={() => modal.hide()} size="small" aria-label="Close">
          <IconClose width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{p: "0 24px 20px", display: "flex", flexDirection: "column", gap: 1.5}}>
        <Text size={14}>{message}</Text>
        <Text size={13} sx={{color: Colors.grey._700}}>
          Remove it from the triggers below first, then try again.
        </Text>
        {templates.map((template) => (
          <Box key={template.id} sx={{display: "flex", flexDirection: "column", gap: 1}}>
            {!single && (
              <Text size={13} variant="inter600" sx={{mt: 0.5}}>
                {template.name}
              </Text>
            )}
            {template.triggers.map((reference) => (
              <TriggerReferenceRow
                key={`${template.id}-${reference.trigger_id}-${reference.priority}-${reference.slot}`}
                reference={reference}
              />
            ))}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{p: "16px 24px", borderTop: `1px solid ${Colors.tertiary._200}`, gap: 1}}>
        <Button buttonType="outlined-primary" size="small" onClick={() => modal.hide()} sx={mutedButtonSx}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export const showTemplatesInUse = (action: TemplateInUseAction, templates: TemplateInUse[]): boolean => {
  const used = templates.filter((template) => template.triggers.length);
  if (!used.length) return false;
  NiceModal.show(TemplateInUseDialog, {action, templates: used});
  return true;
};

export default TemplateInUseDialog;
